import { useCallback, useState } from "react";
import { useLocale, useService } from "../../../../hooks";

export const useMediaBox = ({ usage, image, setimage, id }) => {
  const { t } = useLocale();
  const { showAlert, openModal, closeModal } = useService("Misc");
  const { fetchByAttr, fetchAll } = useService("Data", "media");
  const [searchKey, setSearchKey] = useState<string>("");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [tableData, setTableData] = useState([]);
  const inputId = "image-input" + id;

  const updateImage = useCallback((val: string) => {
    if (typeof setimage === 'function') {
      setimage(val);
    }
  }, [setimage]);

  const getMedias = useCallback(async () => {
    let result;
    if (usage) {
      result = await fetchByAttr({ usage: { $in: usage } });
    } else {
      result = await fetchAll();
    }
    if (result.status) {
      if (result.data?.length === 0) {
        showAlert({ type: "error", msg: t("mediaNotFound") });
      } else {
        let media = result.data.filter((e) => {
          return e.fileName.includes(".");
        });
        setMediaFiles(media);
        setTableData(media);
      }
    }
  }, [fetchAll, fetchByAttr, showAlert, t, usage]);

  const resetSearch = useCallback(() => {
    setMediaFiles(tableData);
    setSearchKey("");
  }, [tableData]);

  const selectImage = useCallback(
    (file) => {
      let imagesArray = [];
      try {
        imagesArray = image ? JSON.parse(image) : [];
        if (!Array.isArray(imagesArray)) imagesArray = [];
      } catch (error) {
        imagesArray = [];
      }

      if (imagesArray.includes(file)) {
        imagesArray = imagesArray.filter((f) => f !== file);
      } else {
        imagesArray.push(file);
      }
      updateImage(JSON.stringify(imagesArray));
    },
    [image, updateImage]
  );

  const trashSelectedImage = useCallback(
    (item) => {
      try {
        const currentImages = image ? JSON.parse(image) : [];
        if (Array.isArray(currentImages)) {
          const newImages = currentImages.filter((i) => i !== item);
          updateImage(JSON.stringify(newImages));
        }
      } catch (error) {
        console.error("Error parsing image JSON", error);
      }
    },
    [image, updateImage]
  );

  const setImageInput = useCallback(
    (e) => updateImage(e.target.value),
    [updateImage]
  );

  const showImages = useCallback(() => {
    getMedias();
    openModal("media-modal" + id);
  }, [getMedias, id, openModal]);

  const closeMediaModel = useCallback(
    () => closeModal("media-modal" + id),
    [closeModal, id]
  );

  const search = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchKey(value);
      if (!value) {
        setMediaFiles(tableData);
        return;
      }
      const lowerSearchKey = value.toLowerCase();
      const filteredList = tableData.filter((item) =>
        item.fileName.toLowerCase().includes(lowerSearchKey)
      );
      setMediaFiles(filteredList);
    },
    [tableData]
  );

  return {
    search,
    searchKey,
    mediaFiles,
    closeMediaModel,
    setImageInput,
    showImages,
    resetSearch,
    selectImage,
    trashSelectedImage,
    inputId,
  };
};
